import { useState } from 'react';
import { X } from 'lucide-react';
import { businessRulesService, BusinessRule, RuleSeverity } from '../../services/business-rules.service';

interface EditRuleModalProps {
  rule: BusinessRule;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditRuleModal({ rule, onClose, onSuccess }: EditRuleModalProps) {
  const [name, setName] = useState(rule.name);
  const [description, setDescription] = useState(rule.description || '');
  const [severity, setSeverity] = useState<RuleSeverity>(rule.severity);
  const [isActive, setIsActive] = useState(rule.isActive);
  const [isSaving, setIsSaving] = useState(false);

  // Extract current condition values
  const conditions = rule.conditions || {};

  // Shift Rules fields
  const [minShiftHours, setMinShiftHours] = useState(conditions.minShiftHours || 4);
  const [maxShiftHours, setMaxShiftHours] = useState(conditions.maxShiftHours || 12);
  const [breakAfterHours, setBreakAfterHours] = useState(conditions.breakAfterHours || 4);
  const [breakDuration, setBreakDuration] = useState(conditions.breakDuration || 30);
  const [allowSplitShifts, setAllowSplitShifts] = useState(conditions.allowSplitShifts || false);

  // Agent Rules fields
  const [maxWeeklyHours, setMaxWeeklyHours] = useState(conditions.maxWeeklyHours || 40);
  const [maxConsecutiveDays, setMaxConsecutiveDays] = useState(conditions.maxConsecutiveDays || 5);
  const [preferredDailyHours, setPreferredDailyHours] = useState(conditions.preferredDailyHours || 8);
  const [maxDailyHours, setMaxDailyHours] = useState(conditions.maxDailyHours || 10);

  // Compliance fields
  const [minRestHours, setMinRestHours] = useState(conditions.minRestHours || 11);
  const [weekendRotationWeeks, setWeekendRotationWeeks] = useState(conditions.weekendRotationWeeks || 2);
  const [maxWeekendDays, setMaxWeekendDays] = useState(conditions.maxWeekendDays || 1);

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Please enter a rule name');
      return;
    }

    setIsSaving(true);
    try {
      const updatedConditions = buildConditions();

      await businessRulesService.update(rule.id, {
        name,
        description: description || undefined,
        severity,
        isActive,
        conditions: updatedConditions,
      });

      onSuccess();
    } catch (error) {
      console.error('Failed to update rule:', error);
      alert('Failed to update rule. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const buildConditions = () => {
    switch (rule.ruleType) {
      case 'CHANNEL_HOURS':
      case 'CAPACITY_TARGET':
        return {
          minShiftHours,
          maxShiftHours,
          breakAfterHours,
          breakDuration,
          allowSplitShifts,
        };
      case 'AGENT_CONSTRAINT':
      case 'TEAM_CONSTRAINT':
        return {
          maxWeeklyHours,
          maxConsecutiveDays,
          preferredDailyHours,
          maxDailyHours,
        };
      case 'LABOR_LAW':
        return {
          minRestHours,
          weekendRotationWeeks,
          maxWeekendDays,
        };
      default:
        return conditions;
    }
  };

  const isShiftRule = rule.ruleType === 'CHANNEL_HOURS' || rule.ruleType === 'CAPACITY_TARGET';
  const isAgentRule = rule.ruleType === 'AGENT_CONSTRAINT' || rule.ruleType === 'TEAM_CONSTRAINT';
  const isComplianceRule = rule.ruleType === 'LABOR_LAW';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Edit Business Rule</h2>
            <p className="text-sm text-gray-600 mt-1">
              Type: {rule.ruleType.replace(/_/g, ' ')} • Scope: {rule.scope}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rule Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Severity
              </label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as RuleSeverity)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="INFO">Info - Informational only</option>
                <option value="WARNING">Warning - Show warning</option>
                <option value="ERROR">Error - Prevent action</option>
                <option value="CRITICAL">Critical - Hard constraint</option>
              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2 pt-7">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">Rule is active</span>
              </label>
            </div>
          </div>

          {/* Type-specific fields */}
          {isShiftRule && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Shift Constraints</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Shift Length (hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={minShiftHours}
                    onChange={(e) => setMinShiftHours(parseInt(e.target.value) || 4)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Shift Length (hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={maxShiftHours}
                    onChange={(e) => setMaxShiftHours(parseInt(e.target.value) || 12)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Break Required After (hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={breakAfterHours}
                    onChange={(e) => setBreakAfterHours(parseInt(e.target.value) || 4)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Break Duration (minutes)
                  </label>
                  <input
                    type="number"
                    min="15"
                    max="120"
                    step="15"
                    value={breakDuration}
                    onChange={(e) => setBreakDuration(parseInt(e.target.value) || 30)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={allowSplitShifts}
                      onChange={(e) => setAllowSplitShifts(e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Allow split shifts</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {isAgentRule && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Agent Constraints</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Weekly Hours
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="168"
                    value={maxWeeklyHours}
                    onChange={(e) => setMaxWeeklyHours(parseInt(e.target.value) || 40)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Consecutive Days
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="14"
                    value={maxConsecutiveDays}
                    onChange={(e) => setMaxConsecutiveDays(parseInt(e.target.value) || 5)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Daily Hours
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={preferredDailyHours}
                    onChange={(e) => setPreferredDailyHours(parseInt(e.target.value) || 8)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Daily Hours
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={maxDailyHours}
                    onChange={(e) => setMaxDailyHours(parseInt(e.target.value) || 10)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {isComplianceRule && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Compliance Requirements</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Rest Between Shifts (hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={minRestHours}
                    onChange={(e) => setMinRestHours(parseInt(e.target.value) || 11)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">EU requires 11 hours</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weekend Rotation (weeks)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={weekendRotationWeeks}
                    onChange={(e) => setWeekendRotationWeeks(parseInt(e.target.value) || 2)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Weekend Days per Period
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="8"
                    value={maxWeekendDays}
                    onChange={(e) => setMaxWeekendDays(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {isSaving && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
